export interface SubTypeUI {
    icon: string;
    description: string;
}
import { Injectable, signal } from '@angular/core';

export interface MapMarker {
    lat: number;
    lng: number;
    title?: string;
    placeId?: string;
}

export interface ServiceMapState {
    center: { lat: number | undefined; lng: number | undefined; };
    realUserLocation?: { lat: number | undefined; lng: number | undefined; };
    markers: MapMarker[];
    zoom: number;
    searchQuery?: string;
    locationName?: string;
    serviceName?: string;
    resultsCount?: number;
    businessResults?: any[];
}

@Injectable({ providedIn: 'root' })
export class ServiceMapStateService {
    /** Null means no search has been done yet */
    readonly mapState = signal<ServiceMapState | null>(null);

    /** True when the user has navigated from the popup to Service Hub */
    readonly showMapInSidebar = signal<boolean>(false);

    /** Stores feedback objects by placeId for cross-view persistence */
    readonly feedbackCache = new Map<string, any>();

    private readonly PERSISTENT_CACHE_KEY = 'pra_persistent_feedback_cache';

    constructor() {
        this.loadCacheFromStorage();
    }

    private loadCacheFromStorage(): void {
        try {
            const stored = localStorage.getItem(this.PERSISTENT_CACHE_KEY);
            if (stored) {
                const data = JSON.parse(stored);
                Object.entries(data).forEach(([key, value]) => {
                    this.feedbackCache.set(key, value);
                });
            }
        } catch (e) {
            console.error('Failed to load persistent feedback cache', e);
        }
    }

    /** Sets feedback in memory and persists to localStorage */
    setFeedbackPersistently(placeId: string, feedback: any): void {
        this.feedbackCache.set(placeId, feedback);
        this.saveCacheToStorage();
    }

    private saveCacheToStorage(): void {
        try {
            const data = Object.fromEntries(this.feedbackCache.entries());
            // Filter out 'loading' states before saving
            const toSave = Object.keys(data).reduce((acc: any, key) => {
                if (data[key] !== 'loading') {
                    acc[key] = data[key];
                }
                return acc;
            }, {});
            localStorage.setItem(this.PERSISTENT_CACHE_KEY, JSON.stringify(toSave));
        } catch (e) {
            console.error('Failed to save persistent feedback cache', e);
        }
    }

    /** Triggered when a recommendation is successful to show overlay on map */
    readonly recommendSuccess = signal<boolean>(false);

    triggerRecommendSuccess(): void {
        this.recommendSuccess.set(true);
        setTimeout(() => this.recommendSuccess.set(false), 2000);
    }

    setMapState(state: ServiceMapState): void {
        this.mapState.set(state);
    }

    activateSidebarMap(): void {
        this.showMapInSidebar.set(true);
    }

    deactivateSidebarMap(): void {
        this.showMapInSidebar.set(false);
    }

    clearMapState(): void {
        this.mapState.set(null);
        this.showMapInSidebar.set(false);
    }

    /** True when the request detail "View Maps" is active */
    readonly showRequestMap = signal<boolean>(false);

    showRequestMapView(): void {
        this.showRequestMap.set(true);
    }

    hideRequestMapView(): void {
        this.showRequestMap.set(false);
    }
}
export const SERVICE_UI_MAP: Record<string, Record<string, SubTypeUI>> = {

    /* ===================== LAUNDRY ===================== */
    laundry: {
        wash_and_fold: {
            icon: 'assets/icons/fill-icons/laundry.svg',
            description: 'Professional washing and neatly folded laundry service.'
        },
        dry_clean: {
            icon: 'assets/icons/fill-icons/laundry.svg',
            description: 'Premium dry cleaning for delicate garments.'
        },
        hang_dry: {
            icon: 'assets/icons/fill-icons/laundry.svg',
            description: 'Air-dried laundry to preserve fabric quality.'
        },
        washing_machine: {
            icon: 'assets/icons/fill-icons/laundry.svg',
            description: 'Washing machine repair and servicing.'
        }
    },

    /* ===================== CLEANING ===================== */
    cleaning: {
        room: {
            icon: 'assets/icons/fill-icons/cleaning.svg',
            description: 'Complete cleaning service for individual rooms.'
        },
        studio: {
            icon: 'assets/icons/fill-icons/cleaning.svg',
            description: 'Deep cleaning tailored for studio apartments.'
        },
        apartment: {
            icon: 'assets/icons/fill-icons/cleaning.svg',
            description: 'Comprehensive apartment cleaning solutions.'
        },
        house: {
            icon: 'assets/icons/fill-icons/cleaning.svg',
            description: 'Full house cleaning including all living spaces.'
        },
        office: {
            icon: 'assets/icons/fill-icons/cleaning.svg',
            description: 'Professional office cleaning services.'
        }
    },

    /* ===================== PET CARE ===================== */
    pet_care: {
        dog_walking: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Daily dog walking by trusted caregivers.'
        },
        in_home_pet_sitting: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Pet sitting in the comfort of your home.'
        },
        pet_sitting: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Reliable pet sitting services.'
        },
        grooming: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Professional pet grooming services.'
        },
        pet_care: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Complete pet care and wellness services.'
        },
        vet_services: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Veterinary and medical services for pets.'
        }
    },

    /* ===================== HANDYMAN ===================== */
    handyman_moving: {
        general_repairs: {
            icon: 'assets/icons/fill-icons/handyman.png',
            description: 'Fixing a wide range of household issues.'
        },
        electrical: {
            icon: 'assets/icons/fill-icons/cleaning.svg',
            description: 'Installing and repairing electrical systems.'
        },
        plumbing: {
            icon: 'assets/icons/fill-icons/beauty.svg',
            description: 'Fixing pipes, faucets, and water systems.'
        },
        carpentry: {
            icon: 'assets/icons/fill-icons/photo.svg',
            description: 'Furniture repair and custom woodwork.'
        },
        painting: {
            icon: 'assets/icons/fill-icons/personal.svg',
            description: 'Interior and exterior painting services.'
        },
        moving: {
            icon: 'assets/icons/fill-icons/handyman.svg',
            description: 'Professional relocation and moving services.'
        },
        // ADDED
        tile_flooring: {
            icon: 'assets/icons/fill-icons/automotive.svg',
            description: 'Installing, repairing, or replacing tiles and flooring.'
        },
        landscaping: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Caring and maintenance for and enhancing outdoor spaces.'
        },
        assembly_installation: {
            icon: 'assets/icons/fill-icons/handyman.svg',
            description: 'Building furniture and setting up equipment or appliances.'
        },
        home_improvement: {
            icon: 'assets/icons/fill-icons/healthcare.svg',
            description: 'Renovating or upgrading spaces.'
        },
        hvac_maintenance: {
            icon: 'assets/icons/fill-icons/tutoring.svg',
            description: 'Inspecting and servicing heating, ventilation, and air conditioning systems.'
        },
        roofing_repair: {
            icon: 'assets/icons/fill-icons/freelancer.svg',
            description: 'Addressing leaks, damage, or wear on roofs.'
        }
    },

    /* ===================== PHOTO & VIDEO ===================== */
    photo_video: {
        photography: {
            icon: 'assets/icons/fill-icons/photography.png',
            description: 'Capturing high-quality images for various purposes.'
        },
        videography: {
            icon: 'assets/icons/fill-icons/cleaning.svg',
            description: 'Professional video production services.'
        },
        editing: {
            icon: 'assets/icons/fill-icons/beauty.svg',
            description: 'Photo and video editing services.'
        },
        drone_services: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Aerial photography and videography.'
        },
        virtual_tours: {
            icon: 'assets/icons/fill-icons/handyman.svg',
            description: 'Interactive 360° virtual tour creation.'
        }
    },

    /* ===================== HEALTHCARE ===================== */
    healthcare_homeaide: {
        home_health_aide: {
            icon: 'assets/icons/fill-icons/healthcare.svg',
            description: 'In-home healthcare assistance.'
        },
        personal_care_aide: {
            icon: 'assets/icons/fill-icons/healthcare.svg',
            description: 'Personal daily care support.'
        },
        certified_therapy_aide: {
            icon: 'assets/icons/fill-icons/healthcare.svg',
            description: 'Support for physical and therapy services.'
        },
        hospice_aide: {
            icon: 'assets/icons/fill-icons/healthcare.svg',
            description: 'Compassionate hospice care assistance.'
        }
    },

    /* ===================== TUTORING ===================== */
    tutoring: {
        one_on_one_tutoring: {
            icon: 'assets/icons/fill-icons/tutoring.svg',
            description: 'Personalized one-on-one tutoring.'
        },
        group_tutoring: {
            icon: 'assets/icons/fill-icons/tutoring.svg',
            description: 'Collaborative group tutoring sessions.'
        },
        online_tutoring: {
            icon: 'assets/icons/fill-icons/tutoring.svg',
            description: 'Flexible online tutoring services.'
        },
        in_home_tutoring: {
            icon: 'assets/icons/fill-icons/tutoring.svg',
            description: 'Private tutoring at your home.'
        }
    },

    /* ===================== PERSONAL SERVICES ===================== */
    personal_services: {
        personal_assistant: {
            icon: 'assets/icons/fill-icons/personal.png',
            description: 'Administrative and personal support.'
        },
        personal_training: {
            icon: 'assets/icons/fill-icons/personal.png',
            description: 'Custom fitness coaching programs.'
        },
        personal_chef: {
            icon: 'assets/icons/fill-icons/photo.svg',
            description: 'Private chef services at your home.'
        },
        // FIXED: was 'chauffeur', API uses 'chauffeur_service'
        chauffeur_service: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Professional drivers who transport clients in luxury vehicles.'
        },
        // ADDED
        personal_shopping: {
            icon: 'assets/icons/fill-icons/personal.png',
            description: 'Assistance with shopping for clothing, gifts, and other items.'
        },
        aromatherapy: {
            icon: 'assets/icons/fill-icons/personal.svg',
            description: 'The use of essential oils for therapeutic benefits, such as relaxation and stress relief.'
        },
        career_counseling: {
            icon: 'assets/icons/fill-icons/automotive.svg',
            description: 'Guidance provided to individuals to help them choose or change their career paths.'
        },
        childcare_service: {
            icon: 'assets/icons/fill-icons/personal.svg',
            description: 'Care provided for children, including babysitting and daycare.'
        },
        party_planning: {
            icon: 'assets/icons/fill-icons/personal.svg',
            description: 'Organizers who plan and execute parties and events for children.'
        },
        concierge_service: {
            icon: 'assets/icons/fill-icons/tutoring.svg',
            description: 'Personalized assistance with various tasks, such as booking reservations and travel.'
        },
        counseling_service: {
            icon: 'assets/icons/fill-icons/freelancer.svg',
            description: 'Professional guidance to help manage personal, social, or psychological challenges.'
        },
        home_security_service: {
            icon: 'assets/icons/fill-icons/personal.svg',
            description: 'Installation and monitoring of security systems for homes.'
        },
        life_coaching: {
            icon: 'assets/icons/fill-icons/freelancer.svg',
            description: 'Guidance and support to help individuals achieve personal and professional goals.'
        },
        massage_therapy: {
            icon: 'assets/icons/fill-icons/freelancer.svg',
            description: 'Therapeutic massage to relieve stress, pain, and muscle tension.'
        },
        tattoo_service: {
            icon: 'assets/icons/fill-icons/automotive.svg',
            description: 'Artistic designs permanently inked onto the skin.'
        },
        tech_support_service: {
            icon: 'assets/icons/fill-icons/personal.svg',
            description: 'Assistance with technical issues and electronic devices.'
        },
        wardrobe_stylist: {
            icon: 'assets/icons/fill-icons/freelancer.svg',
            description: 'Professionals who help clients choose and coordinate outfits.'
        }
    },

    /* ===================== FREELANCE ===================== */
    freelance_work: {
        web_creative_design: {
            icon: 'assets/icons/fill-icons/freelancer.svg',
            description: 'Designs clean, modern websites and visuals.'
        },
        writing_content_creation: {
            icon: 'assets/icons/fill-icons/freelancer.svg',
            description: 'Creates clear, engaging written content.'
        },
        marketing_social_media: {
            icon: 'assets/icons/fill-icons/freelancer.svg',
            description: 'Drives brand growth through strategic marketing and social content.'
        }
    },

    /* ===================== AUTOMOTIVE ===================== */
    automotive_services: {
        on_site_visit: {
            icon: 'assets/icons/fill-icons/automotive.svg',
            description: 'On-site car repair and inspection.'
        },
        schedule_shop_visit: {
            icon: 'assets/icons/fill-icons/automotive.svg',
            description: 'Book an appointment at a repair shop.'
        },
        emergency_repair: {
            icon: 'assets/icons/fill-icons/automotive.svg',
            description: 'Emergency roadside repair services.'
        }
    },

    /* ===================== EVENT ===================== */
    event_services: {
        catering: {
            icon: 'assets/icons/fill-icons/catering.png',
            description: 'Providing food and beverage services for events and gatherings.'
        },
        // FIXED: was 'personal_chef', API uses 'event_personal_chef'
        event_personal_chef: {
            icon: 'assets/icons/fill-icons/cleaning.svg',
            description: 'Chefs who prepare meals for clients in their homes.'
        },
        wedding_planning: {
            icon: 'assets/icons/fill-icons/beauty.svg',
            description: 'Organizing and coordinating all aspects of a wedding.'
        },
        // FIXED: was 'djing', API uses 'dj_service'
        dj_service: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Providing music selection and mixing services for events.'
        },
        corporate_event: {
            icon: 'assets/icons/fill-icons/handyman.svg',
            description: 'Organizing and managing corporate events.'
        }
    },

    /* ===================== LEGAL ===================== */
    legal_services: {
        traffic_law: {
            icon: 'assets/icons/fill-icons/legal-listing.png',
            description: 'Handling traffic law cases and violations.'
        },
        corporate_law: {
            icon: 'assets/icons/fill-icons/cleaning.svg',
            description: 'Business and corporate legal services.'
        },
        contract_law: {
            icon: 'assets/icons/fill-icons/beauty.svg',
            description: 'Drafting and reviewing legal contracts.'
        },
        litigation: {
            icon: 'assets/icons/fill-icons/photo.svg',
            description: 'Representation in court proceedings.'
        },
        family_law: {
            icon: 'assets/icons/fill-icons/personal.svg',
            description: 'Divorce, custody, and family matters.'
        },
        estate_planning: {
            icon: 'assets/icons/fill-icons/automotive.svg',
            description: 'Estate planning and asset management.'
        },
        // FIXED: was 'ip_law', API uses 'intellectual_property_law'
        intellectual_property_law: {
            icon: 'assets/icons/fill-icons/legal.svg',
            description: 'Protecting trademarks, patents, and copyrights.'
        },
        // ADDED
        real_estate_law: {
            icon: 'assets/icons/fill-icons/personal.svg',
            description: 'Handling property transactions, leases, and disputes.'
        },
        immigration_law: {
            icon: 'assets/icons/fill-icons/automotive.svg',
            description: 'Assisting with visas, green cards, and citizenship applications.'
        },
        bankruptcy_law: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Assisting individuals and businesses in debt relief and bankruptcy filings.'
        },
        personal_injury_law: {
            icon: 'assets/icons/fill-icons/handyman.svg',
            description: 'Representing clients who have been injured due to negligence or accidents.'
        },
        notary_service: {
            icon: 'assets/icons/fill-icons/healthcare.svg',
            description: 'Certifying documents and administering oaths.'
        },
        mediation_arbitration: {
            icon: 'assets/icons/fill-icons/tutoring.svg',
            description: 'Facilitating dispute resolution outside of court.'
        }
    },

    /* ===================== YARD WORK ===================== */
    yard_work: {
        lawn_care: {
            icon: 'assets/icons/fill-icons/yard-listing.svg',
            description: 'Maintenance of lawns, including mowing, fertilizing, and weed control.'
        },
        pool_maintenance_and_care: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Maintenance of pool, including cleaning and repair.'
        }
    },

    /* ===================== BUSINESS SERVICES ===================== */
    business_services: {
        tax_preparation_services: {
            icon: 'assets/icons/fill-icons/business-service.svg',
            description: 'Professional tax preparation and filing services.'
        }
    },

    /* ===================== HOME IMPROVEMENT ===================== */
    home_improvement: {
        interior_and_exterior_improvements: {
            icon: 'assets/icons/fill-icons/home-improvement.svg',
            description: 'Upgrading and enhancing interior and exterior spaces.'
        },
        energy_efficiency: {
            icon: 'assets/icons/fill-icons/home-improvement.svg',
            description: 'Energy-saving upgrades and efficiency solutions.'
        },
        maintenance_and_repairs: {
            icon: 'assets/icons/fill-icons/home-improvement.svg',
            description: 'General home maintenance and repair services.'
        }
    },

    /* ===================== HEALTH & BEAUTY ===================== */
    health_beauty_services: {
        spa: {
            icon: 'assets/icons/fill-icons/barber.png',
            description: 'Relaxing spa treatments for body and mind.'
        },
        nails: {
            icon: 'assets/icons/fill-icons/beauty.svg',
            description: 'Professional nail care and styling services.'
        },
        care: {
            icon: 'assets/icons/fill-icons/beauty.svg',
            description: 'Complete beauty and personal care services.'
        },
        skin_care: {
            icon: 'assets/icons/fill-icons/beauty.svg',
            description: 'Advanced skincare treatments and solutions.'
        },
        // ADDED
        barber: {
            icon: 'assets/icons/fill-icons/barber.png',
            description: 'Professionals who cut, trim, and style hair, primarily for men.'
        },
        manicure_pedicure: {
            icon: 'assets/icons/fill-icons/cleaning.svg',
            description: 'Nail care services, including trimming, shaping, and polishing.'
        },
        makeup_service: {
            icon: 'assets/icons/fill-icons/beauty.svg',
            description: 'Professional application of makeup for various occasions.'
        },
        waxing_hair_removal: {
            icon: 'assets/icons/fill-icons/photo.svg',
            description: 'Services that remove unwanted hair from the body.'
        },
        dietitian_nutritionist: {
            icon: 'assets/icons/fill-icons/personal.svg',
            description: 'Professionals who provide dietary advice and nutrition plans.'
        },
        hair_stylist: {
            icon: 'assets/icons/fill-icons/petcare.svg',
            description: 'Professionals who cut, color, and style hair.'
        },
        beauty_salon: {
            icon: 'assets/icons/fill-icons/handyman.svg',
            description: 'Relieves discomfort and maintains hygiene.'
        },
        bridal_beauty: {
            icon: 'assets/icons/fill-icons/healthcare.svg',
            description: 'Specialized beauty treatments for brides, including hair, makeup, and skincare.'
        },
        yoga_instruction: {
            icon: 'assets/icons/fill-icons/tutoring.svg',
            description: 'Teaching yoga poses and techniques for physical and mental well-being.'
        }
    },
};